<template>
    <div>
        <div
            v-for="field in general_fields"
            :key="field.path + ':' + field.key"
            class="form-group mt-3"
        >
            <template v-if="field.type === 'object'">
                <div class="border rounded p-3">
                    <div class="font-weight-bold mb-3">
                        {{ __t(field.title) }}
                    </div>

                    <div
                        v-for="subKey in field.children"
                        :key="field.key + ':' + subKey"
                        class="form-group mt-3"
                    >
                        <template v-if="isTranslatedObject(getNestedFieldValue(field, subKey))">
                            <div class="font-weight-bold mb-2">
                                {{ __t(subKey.toUpperCase()) }}
                            </div>

                            <div
                                v-for="lang in translationVisibleLanguages"
                                :key="field.key + ':' + subKey + ':' + lang"
                                class="form-group mt-2"
                            >
                                <label class="form-label">
                                    {{ __t(subKey.toUpperCase()) }} ({{ lang.toUpperCase() }})
                                </label>

                                <input
                                    type="text"
                                    class="form-control"
                                    :value="getTranslatedNestedFieldValue(field, subKey, lang)"
                                    @input="setTranslatedNestedFieldValue(field, subKey, lang, $event.target.value)"
                                />
                            </div>
                        </template>

                        <template v-else>
                            <label class="form-label">
                                {{ __t(subKey.toUpperCase()) }}
                            </label>

                            <input
                                type="text"
                                class="form-control"
                                :value="getNestedFieldValue(field, subKey)"
                                @input="setNestedFieldValue(field, subKey, $event.target.value)"
                            />
                        </template>
                    </div>
                </div>
            </template>

            <template v-else-if="field.key === 'city_id'">
                <label class="form-label">
                    {{ __t(field.title) }}
                </label>

                <v-select
                    :value="selectedCity"
                    @input="onCityChange"
                    label="name"
                    :options="cities"
                    :filterable="false"
                    :clearable="true"
                />
            </template>

            <template v-else-if="field.key === 'category_code'">
                <label class="form-label">
                    {{ __t(field.title) }}
                </label>

                <v-select
                    :value="selectedCategory"
                    @input="onCategoryChange"
                    label="code"
                    :options="categories"
                    :filterable="false"
                    :clearable="true"
                />
            </template>

            <template v-else-if="field.type === 'translated'">
                <div class="border rounded p-3">
                    <div class="font-weight-bold mb-3">
                        {{ __t(field.title) }}
                    </div>

                    <div
                        v-for="lang in translationVisibleLanguages"
                        :key="field.key + ':' + lang"
                        class="form-group mt-2"
                    >
                        <label class="form-label">
                            {{ __t(field.title) }} ({{ lang.toUpperCase() }})
                        </label>

                        <input
                            type="text"
                            class="form-control"
                            :value="getTranslatedFieldValue(field, lang)"
                            @input="setTranslatedFieldValue(field, lang, $event.target.value)"
                        />
                    </div>
                </div>
            </template>

            <template v-else>
                <label class="form-label">
                    {{ __t(field.title) }}
                </label>

                <input
                    type="text"
                    class="form-control"
                    :value="getFieldValue(field)"
                    @input="setFieldValue(field, $event.target.value)"
                />
            </template>
        </div>
    </div>
</template>

<script>
import vSelect from 'vue-select';

export default {
    name: "uklon_market_seller",

    components: {
        vSelect
    },

    props: {
        location: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            cities: [],
            categories: [],
            languages: ['uk', 'en'],
            translationVisibleLanguages: ['uk', 'en'],
        };
    },

    created() {
        if(parseInt(this.location.id) > 0) {
            this.ensureStructure();
            this.loadCategories();
            this.loadCities();
        }
    },

    methods: {
        ensureStructure() {
            if (!this.location.general_fields) {
                this.$set(this.location, 'general_fields', {});
            }

            if (!this.location.general_fields.uklon_data) {
                this.$set(this.location.general_fields, 'uklon_data', {});
            }
        },

        isPlainObject(value) {
            return value && typeof value === 'object' && !Array.isArray(value);
        },

        isTranslatedObject(value) {
            if (!this.isPlainObject(value)) return false;

            const keys = Object.keys(value);
            if (!keys.length) return false;

            return this.languages.some(lang => keys.includes(lang));
        },

        async loadCities() {
            this.cities = [];

            await agr_api.get('/aggregators/uklon-market/' + this.location.id + '/cities')
                .then(({ data }) => {
                    if (data.status === "operation_success") {
                        this.cities = data.data || [];
                    }
                })
                .catch(({ response }) => {
                    EventBus.$emit('show_toast', {
                        err: response?.data?.message || 'Load cities error',
                        type: "danger",
                        title: response?.data?.status || 'error'
                    });
                });
        },

        async loadCategories() {
            this.categories = [];

            await agr_api.get('/aggregators/uklon-market/' + this.location.id + '/categories')
                .then(({ data }) => {
                    if (data.status === "operation_success") {
                        this.categories = data.data || [];
                    }
                })
                .catch(({ response }) => {
                    EventBus.$emit('show_toast', {
                        err: response?.data?.message || 'Load categories error',
                        type: "danger",
                        title: response?.data?.status || 'error'
                    });
                });
        },

        getFieldRoot(field) {
            if (field.path === 'base') {
                return this.location.general_fields;
            }

            if (field.path === 'uklon_data') {
                this.ensureStructure();
                return this.location.general_fields.uklon_data;
            }

            return null;
        },

        getFieldValue(field) {
            const root = this.getFieldRoot(field);
            return root ? root[field.key] : '';
        },

        setFieldValue(field, value) {
            const root = this.getFieldRoot(field);
            if (!root) return;

            this.$set(root, field.key, value);
        },

        getTranslatedFieldValue(field, lang) {
            const root = this.getFieldRoot(field);
            if (!root) return '';

            if (!this.isPlainObject(root[field.key])) {
                this.$set(root, field.key, {});
            }

            if (root[field.key][lang] === undefined) {
                this.$set(root[field.key], lang, '');
            }

            return root[field.key][lang] ?? '';
        },

        setTranslatedFieldValue(field, lang, value) {
            const root = this.getFieldRoot(field);
            if (!root) return;

            if (!this.isPlainObject(root[field.key])) {
                this.$set(root, field.key, {});
            }

            this.$set(root[field.key], lang, value);
        },

        getNestedFieldValue(field, subKey) {
            const root = this.getFieldRoot(field);
            if (!root) return '';

            if (!this.isPlainObject(root[field.key])) {
                this.$set(root, field.key, {});
            }

            return root[field.key][subKey] ?? '';
        },

        setNestedFieldValue(field, subKey, value) {
            const root = this.getFieldRoot(field);
            if (!root) return;

            if (!this.isPlainObject(root[field.key])) {
                this.$set(root, field.key, {});
            }

            this.$set(root[field.key], subKey, value);
        },

        getTranslatedNestedFieldValue(field, subKey, lang) {
            const root = this.getFieldRoot(field);
            if (!root) return '';

            if (!this.isPlainObject(root[field.key])) {
                this.$set(root, field.key, {});
            }

            if (!this.isPlainObject(root[field.key][subKey])) {
                this.$set(root[field.key], subKey, {});
            }

            if (root[field.key][subKey][lang] === undefined) {
                this.$set(root[field.key][subKey], lang, '');
            }

            return root[field.key][subKey][lang] ?? '';
        },

        setTranslatedNestedFieldValue(field, subKey, lang, value) {
            const root = this.getFieldRoot(field);
            if (!root) return;

            if (!this.isPlainObject(root[field.key])) {
                this.$set(root, field.key, {});
            }

            if (!this.isPlainObject(root[field.key][subKey])) {
                this.$set(root[field.key], subKey, {});
            }

            this.$set(root[field.key][subKey], lang, value);
        },

        onCityChange(option) {
            this.ensureStructure();
            this.$set(this.location.general_fields.uklon_data, 'city_id', option ? option.id : 0);
        },

        onCategoryChange(option) {
            this.ensureStructure();
            this.$set(this.location.general_fields.uklon_data, 'category_code', option ? option.code : '');
        },
    },

    computed: {
        general_fields() {
            if (!this.location?.general_fields) return [];

            const excluded = ['id', 'app_id', 'app_secret'];

            const mapField = (key, value, path) => {
                if (this.isTranslatedObject(value)) {
                    return {
                        key,
                        path,
                        type: 'translated',
                        title: key.toUpperCase(),
                    };
                }

                if (this.isPlainObject(value)) {
                    return {
                        key,
                        path,
                        type: 'object',
                        title: key.toUpperCase(),
                        children: Object.keys(value),
                    };
                }

                return {
                    key,
                    path,
                    type: 'scalar',
                    title: key.toUpperCase(),
                };
            };

            const base = Object.entries(this.location.general_fields)
                .filter(([key]) => !excluded.includes(key) && key !== 'uklon_data')
                .map(([key, value]) => mapField(key, value, 'base'));

            const uklon = this.location.general_fields.uklon_data || {};

            const uklonFields = Object.entries(uklon)
                .filter(([key]) => !excluded.includes(key))
                .map(([key, value]) => mapField(key, value, 'uklon_data'));

            return [...base, ...uklonFields];
        },

        selectedCity() {
            const cityId = this.location?.general_fields?.uklon_data?.city_id ?? 0;
            if (!cityId) return null;

            return this.cities.find(city => Number(city.id) === Number(cityId)) || null;
        },

        selectedCategory() {
            const code = this.location?.general_fields?.uklon_data?.category_code ?? '';
            if (!code) return null;

            return this.categories.find(item => String(item.code) === String(code)) || null;
        }
    },
}
</script>

<style scoped>
.border {
    border: 1px solid #dee2e6;
}
.rounded {
    border-radius: 6px;
}
.p-3 {
    padding: 1rem;
}
.mt-2 {
    margin-top: 0.5rem;
}
.mt-3 {
    margin-top: 1rem;
}
.mb-3 {
    margin-bottom: 1rem;
}
.font-weight-bold {
    font-weight: 600;
}
</style>
